import { useRef, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { UserCog, CheckCircle2 } from "lucide-react"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import { SettingsTabs } from "@/app/components/SettingsTabs"
import { SettingsCard } from "@/app/components/SettingsCard"
import { UserAvatar } from "@/app/components/UserAvatar"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import {
  ErrorBanner,
  inputStyle,
  labelStyle,
  formatApiError,
} from "@/app/components/FormPrimitives"
import { primaryButton, secondaryButton } from "@/app/lib/buttonStyles"
import { resizeToSquareJpeg } from "@/lib/resizeImage"

// Small helper caption under a field.
function Caption({ children }: { children: React.ReactNode }) {
  const { theme } = useClientTheme()
  return (
    <span style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textDim }}>
      {children}
    </span>
  )
}

export function ProfileSettingsPage() {
  const { theme } = useClientTheme()
  const { user, isLoading } = useCurrentUser()
  const queryClient = useQueryClient()

  const fileInputRef = useRef<HTMLInputElement>(null)
  // Seed the name input from the loaded user, but only once user is present.
  const [name, setName] = useState<string | null>(null)

  const invalidateMe = () => queryClient.invalidateQueries({ queryKey: ["me"] })

  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      const blob = await resizeToSquareJpeg(file)
      const form = new FormData()
      form.append("avatar", blob, "avatar.jpg")
      return api.putForm<{ avatar_url: string }>("/me/avatar", form)
    },
    onSuccess: invalidateMe,
  })

  const removeAvatar = useMutation({
    mutationFn: () => api.del("/me/avatar"),
    onSuccess: invalidateMe,
  })

  const saveName = useMutation({
    mutationFn: (value: string) => api.patch("/me", { user: { name: value } }),
    onSuccess: invalidateMe,
  })

  if (isLoading || !user) {
    return (
      <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
        <div
          className="max-w-[760px] mx-auto"
          style={{ color: theme.textMuted, fontFamily: "var(--font-ui)" }}
        >
          Loading…
        </div>
      </div>
    )
  }

  // The input is controlled; before the first edit `name` is null and we mirror
  // the persisted value. After editing, `name` holds the working draft.
  const nameValue = name ?? user.name
  const nameChanged = nameValue.trim() !== user.name && nameValue.trim().length > 0

  // Superadmins' name/avatar are canonical on their Identity, edited only in
  // the Admin Panel (see Admin::Api::V1::ProfileController) — this page shows
  // them read-only here so there's exactly one place to edit either value.
  const readOnly = user.superadmin

  function handleFile(file: File | undefined | null) {
    if (!file) return
    removeAvatar.reset()
    uploadAvatar.mutate(file)
  }

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div className="max-w-[760px] mx-auto">
        <SettingsTabs />

        <SettingsCard
          icon={<UserCog size={17} strokeWidth={1.75} />}
          title="Profile Settings"
          subtitle="Manage your photo, name, and sign-in email"
        >
          <div className="p-6 flex flex-col gap-8">
            {/* Photo */}
            <section className="flex flex-col gap-3">
              <span style={labelStyle(theme)}>Photo</span>
              <div className="flex items-center gap-4">
                <UserAvatar name={user.name} avatarUrl={user.avatar_url} size={72} />
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      uploadAvatar.reset()
                      fileInputRef.current?.click()
                    }}
                    disabled={readOnly || uploadAvatar.isPending}
                    className="btn"
                    style={{
                      ...secondaryButton(theme),
                      opacity: readOnly || uploadAvatar.isPending ? 0.6 : 1,
                      cursor: readOnly ? "not-allowed" : undefined,
                    }}
                  >
                    {uploadAvatar.isPending ? "Uploading…" : "Upload photo"}
                  </button>
                  {user.avatar_url && (
                    <button
                      type="button"
                      onClick={() => {
                        uploadAvatar.reset()
                        removeAvatar.mutate()
                      }}
                      disabled={readOnly || removeAvatar.isPending}
                      className="btn"
                      style={{
                        ...secondaryButton(theme),
                        color: theme.brandAccent,
                        opacity: readOnly || removeAvatar.isPending ? 0.6 : 1,
                        cursor: readOnly ? "not-allowed" : undefined,
                      }}
                    >
                      {removeAvatar.isPending ? "Removing…" : "Remove"}
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    handleFile(e.target.files?.[0])
                    // Reset so re-selecting the same file re-triggers change.
                    e.target.value = ""
                  }}
                />
              </div>
              {readOnly ? (
                <Caption>
                  Managed in the{" "}
                  <a href="/admin" style={{ color: theme.brandAccent }}>
                    Admin Panel
                  </a>
                  .
                </Caption>
              ) : (
                <Caption>
                  JPG, PNG, or WebP. Photos are cropped to a square and resized automatically.
                </Caption>
              )}
              {uploadAvatar.isError && (
                <ErrorBanner theme={theme} message={formatApiError(uploadAvatar.error)} />
              )}
              {removeAvatar.isError && (
                <ErrorBanner theme={theme} message={formatApiError(removeAvatar.error)} />
              )}
            </section>

            {/* Full name */}
            <section className="flex flex-col gap-3" style={{ maxWidth: "440px" }}>
              <label className="flex flex-col gap-1.5">
                <span style={labelStyle(theme)}>Full name</span>
                <input
                  type="text"
                  value={nameValue}
                  onChange={(e) => setName(e.target.value)}
                  disabled={readOnly}
                  style={{
                    ...inputStyle(theme),
                    ...(readOnly
                      ? {
                          backgroundColor: theme.bgSurface2,
                          color: theme.textMuted,
                          cursor: "not-allowed",
                        }
                      : {}),
                  }}
                />
              </label>
              {readOnly ? (
                <Caption>
                  Managed in the{" "}
                  <a href="/admin" style={{ color: theme.brandAccent }}>
                    Admin Panel
                  </a>
                  .
                </Caption>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      saveName.reset()
                      saveName.mutate(nameValue.trim())
                    }}
                    disabled={!nameChanged || saveName.isPending}
                    className="btn"
                    style={{
                      ...primaryButton(theme),
                      justifyContent: "center",
                      opacity: !nameChanged || saveName.isPending ? 0.6 : 1,
                      cursor: !nameChanged || saveName.isPending ? "default" : "pointer",
                    }}
                  >
                    {saveName.isPending ? "Saving…" : "Save"}
                  </button>
                  {saveName.isSuccess && !nameChanged && (
                    <span
                      className="flex items-center gap-1.5"
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "13px",
                        color: theme.brandTeal,
                      }}
                    >
                      <CheckCircle2 size={15} /> Saved
                    </span>
                  )}
                </div>
              )}
              {saveName.isError && (
                <ErrorBanner theme={theme} message={formatApiError(saveName.error)} />
              )}
            </section>

            {/* Email */}
            <section className="flex flex-col gap-2" style={{ maxWidth: "440px" }}>
              <label className="flex flex-col gap-1.5">
                <span style={labelStyle(theme)}>Email</span>
                <input
                  type="text"
                  value={user.email_address}
                  disabled
                  style={{
                    ...inputStyle(theme),
                    backgroundColor: theme.bgSurface2,
                    color: theme.textMuted,
                    cursor: "not-allowed",
                  }}
                />
              </label>
              <Caption>Contact LandTrack to change your sign-in email.</Caption>
            </section>
          </div>
        </SettingsCard>
      </div>
    </div>
  )
}
